import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGameDialogAccessibility } from './GameDialog';
import { observeElementResize } from './observeElementResize';
import { useDockDialogMotion } from './useDockDialogMotion';
import { GameMessage } from './GameMessage';
import { useGameScrollLock } from './useGameScrollLock';

interface Props {
  mobileControls?: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    row: React.ReactNode;
  };
  fullscreen?: {
    open: boolean;
    blocked?: boolean;
    onMinimize: () => void;
    dockTargetRef: React.RefObject<HTMLButtonElement | null>;
    boundaryRef: React.RefObject<HTMLDivElement | null>;
    controls?: React.ReactNode;
    headerActions?: React.ReactNode;
    onMinimized?: () => void;
  };
  hasActions: boolean;
  hasContext: boolean;
  hasPreviewContext?: boolean;
  message?: string;
  loading: boolean;
  context: React.ReactNode;
  actions: React.ReactNode;
  investigatorSheet?: React.ReactNode;
}

export function GameActionSheet(props: Props) {
  return props.fullscreen ? <FullscreenActionSheet {...props} fullscreen={props.fullscreen} /> : <LegacyActionSheet {...props} />;
}

/** A nonmodal map-sized surface with one live action tree. */
function FullscreenActionSheet({ fullscreen, context, actions, investigatorSheet, message }: Props & { fullscreen: NonNullable<Props['fullscreen']> }) {
  const { open, onMinimize, dockTargetRef, controls } = fullscreen;
  const surface = useRef<HTMLDivElement>(null);
  const backdrop = useRef<HTMLDivElement>(null);
  const visible = useDockDialogMotion(open, dockTargetRef, surface, backdrop);
  useGameScrollLock(open && visible);
  useLayoutEffect(() => {
    if (!visible || !backdrop.current) return;
    const layer = backdrop.current;
    const boundary = fullscreen.boundaryRef.current;
    const dock = document.querySelector<HTMLElement>('.game-dock');
    const measure = () => {
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop ?? 0;
      const viewportBottom = viewportTop + (viewport?.height ?? window.innerHeight);
      const bottom = Math.min(viewportBottom, dock?.getBoundingClientRect().top ?? viewportBottom);
      const top = Math.min(bottom, Math.max(viewportTop, boundary?.getBoundingClientRect().bottom ?? viewportTop));
      layer.style.top = `${top}px`;
      layer.style.height = `${Math.max(0, bottom - top)}px`;
    };
    measure();
    const main = boundary?.parentElement;
    const disconnect = observeElementResize([boundary, main, dock,
      main?.querySelector('.game-summary-status'), main?.querySelector('.game-investigator-controls'),
      document.querySelector('.common-header')], measure);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    window.visualViewport?.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('scroll', measure);
    return () => {
      disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      window.visualViewport?.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('scroll', measure);
    };
  }, [visible, fullscreen.boundaryRef]);
  const wasVisible = useRef(false);
  const minimize = useRef(onMinimize);
  minimize.current = onMinimize;
  const onMinimized = useRef(fullscreen.onMinimized);
  onMinimized.current = fullscreen.onMinimized;
  useEffect(() => {
    if (wasVisible.current && !visible) onMinimized.current?.();
    wasVisible.current = visible;
  }, [visible]);
  useEffect(() => {
    if (!open || !visible) return;
    const dialog = surface.current!;
    const previous = document.activeElement as HTMLElement | null;
    const dockTarget = dockTargetRef.current;
    const opener = previous?.matches('.game-status-actions, .game-dock-turn') ? previous : dockTarget;
    dialog.querySelector<HTMLButtonElement>('.game-dialog-close')?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || document.querySelector('.game-detail-dialog[aria-modal="true"]')) return;
      event.preventDefault();
      event.stopPropagation();
      minimize.current();
    };
    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.removeEventListener('keydown', handleKey, true);
      // Another surface may already have taken focus through a dock/status control.
      if (dialog.contains(document.activeElement) || document.activeElement === document.body) {
        (opener?.isConnected ? opener : dockTarget?.isConnected ? dockTarget : null)?.focus({ preventScroll: true });
      }
    };
  }, [open, visible, dockTargetRef]);
  if (!visible) return null;
  return createPortal(<div ref={backdrop} className={`game-ui game-dialog-backdrop game-fullscreen-actions-backdrop${!open ? ' game-dialog-closing' : ''}`}
    aria-hidden={!open || fullscreen.blocked || undefined} inert={!open || fullscreen.blocked}>
    <div className="game-dialog-scrim" aria-hidden="true" />
    <div ref={surface} className="game-info game-detail-dialog game-fullscreen-actions" role="dialog"
      aria-label="Actions">
      <button type="button" className="game-dialog-close" aria-label="Minimize" title="Minimize" onClick={onMinimize}>−</button>
      {fullscreen.headerActions && <header className="game-fullscreen-actions-header">{fullscreen.headerActions}</header>}
      <div className="game-fullscreen-actions-body">
        <GameMessage message={message} />
        {context}{actions}{investigatorSheet}
      </div>
      {controls && <footer className="game-fullscreen-actions-controls">{controls}</footer>}
    </div>
  </div>, document.body);
}

/** Two snap points, one live action tree, and a reserved footprint below the map. */
function LegacyActionSheet({ hasActions, hasContext, hasPreviewContext = hasContext, message, loading, context, actions, investigatorSheet, mobileControls }: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = mobileControls?.open ?? internalOpen;
  const setOpen = mobileControls?.onOpenChange ?? setInternalOpen;
  const [moving, setMoving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);
  const surface = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const messageElement = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const [messageScrollable, setMessageScrollable] = useState(false);
  const titleId = useId();
  const openRef = useRef(open);
  openRef.current = open;
  const motion = useRef({ y: 0, velocity: 0, collapsed: 0, expandedTop: 0, collapsedTop: 0, left: 0, width: 0, frame: 0, initialized: false });
  const drag = useRef<{ pointer: number; capture: HTMLElement; start: number; startX: number; origin: number; last: number; time: number; velocity: number; active: boolean } | undefined>(undefined);
  const suppressClick = useRef(false);
  const minimizePress = useRef<{ pointer: number; x: number; y: number; moved: boolean } | undefined>(undefined);
  const outsidePress = useRef<{ pointer: number; x: number; y: number; moved: boolean } | undefined>(undefined);
  const outsideClickCleanup = useRef<() => void>(() => {});
  const settleRef = useRef<(expanded: boolean) => void>(() => {});
  const paintRef = useRef<() => void>(() => {});
  const contentResizeRef = useRef<() => void>(() => {});
  const hadActions = useRef(hasActions);
  const messageText = message?.trim();
  const label = messageText || (hasActions ? 'Actions' : 'Details');
  const hasPreview = !mobileControls && (hasActions || hasPreviewContext);
  const available = hasActions || hasContext || !!message?.trim();
  const expandedContent = open || moving || dragging;

  useLayoutEffect(() => {
    const update = () => {
      const node = messageElement.current;
      setMessageScrollable(!!(open && node && node.scrollHeight > node.clientHeight + 1));
    };
    update();
    const disconnect = observeElementResize(messageElement.current, update);
    window.addEventListener('resize', update);
    return () => { disconnect(); window.removeEventListener('resize', update); };
  }, [messageText, open, available]);

  useEffect(() => {
    if (hadActions.current && !hasActions) setOpen(false);
    hadActions.current = hasActions;
    if (!available) setOpen(false);
  }, [hasActions, available, setOpen]);

  // The application stays inert below this modal; nested inspectors portal beside it.
  useEffect(() => {
    if (!open) return;
    const root = document.getElementById('root');
    const wasInert = root?.inert ?? false;
    if (root) root.inert = true;
    return () => { if (root) root.inert = wasInert; };
  }, [open]);
  useGameDialogAccessibility(open && available, available, surface, opener, () => setOpen(false), true);

  const isTopSheet = () => {
    const dialogs = document.querySelectorAll('.game-detail-dialog[aria-modal="true"]');
    return dialogs[dialogs.length - 1] === surface.current;
  };
  useEffect(() => { if (!open) outsidePress.current = undefined; }, [open]);
  useEffect(() => () => outsideClickCleanup.current(), []);

  const consumeOutsideClick = () => {
    outsideClickCleanup.current();
    const cleanup = () => {
      document.removeEventListener('click', block, true);
      document.removeEventListener('pointerdown', cleanup, true);
      outsideClickCleanup.current = () => {};
    };
    const block = (event: MouseEvent) => {
      cleanup();
      if (event.detail > 0) { event.preventDefault(); event.stopImmediatePropagation(); }
    };
    // Collapsing restores hit testing before a delayed compatibility click. Consume
    // that click, but let the next independent pointer or keyboard press work.
    document.addEventListener('click', block, true);
    document.addEventListener('pointerdown', cleanup, true);
    outsideClickCleanup.current = cleanup;
  };

  useLayoutEffect(() => {
    if (!available || !surface.current || !anchor.current) return;
    const panel = surface.current;
    const state = motion.current;
    const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const paint = () => {
      const progress = state.collapsed > 0 ? Math.max(0, Math.min(1, 1 - state.y / state.collapsed)) : openRef.current ? 1 : 0;
      const width = state.width + (window.innerWidth - state.width) * progress;
      const top = state.expandedTop + (state.collapsedTop - state.expandedTop) * state.y / state.collapsed;
      // Keep a stable content layout throughout the gesture; only the compositor
      // handles the sheet's presentation rectangle between its two snap points.
      panel.style.transform = `translate3d(${state.left * (1 - progress)}px, ${top}px, 0) scaleX(${width / window.innerWidth})`;
    };
    paintRef.current = paint;
    const measure = () => {
      const box = anchor.current!.getBoundingClientRect();
      state.left = box.left; state.width = box.width;
      panel.style.left = '0px';
      panel.style.width = `${window.innerWidth}px`;
      panel.style.setProperty('--sheet-peek-height', `${box.height}px`);
      const previousTravel = state.collapsed;
      state.collapsedTop = box.top;
      state.expandedTop = Math.max(0, document.documentElement.clientHeight - panel.offsetHeight);
      // A tiny sheet can end below its preview. Keep upward gestures opening
      // even in that case, and preserve progress when content changes size.
      state.collapsed = Math.max(box.height, box.top - state.expandedTop, 1);
      if (previousTravel > 0) {
        const ratio = state.collapsed / previousTravel;
        const previousY = state.y;
        state.y *= ratio; state.velocity *= ratio;
        if (drag.current) drag.current.origin += state.y - previousY;
      }
      if (!state.initialized) { state.y = state.collapsed; state.initialized = true; }
    };
    const settle = (expanded: boolean) => {
      cancelAnimationFrame(state.frame);
      measure();
      if (drag.current) return;
      const target = expanded ? 0 : state.collapsed;
      if (Math.abs(state.y - target) < 0.1 && Math.abs(state.velocity) < 1) {
        state.y = target; state.velocity = 0; paint();
        panel.dataset.motion = 'settled'; setMoving(false);
        return;
      }
      if (preference?.matches) {
        state.y = target; state.velocity = 0; paint();
        panel.animate?.([{ opacity: 0.7 }, { opacity: 1 }], { duration: 120 });
        panel.dataset.motion = 'settled'; setMoving(false);
        return;
      }
      setMoving(true);
      panel.dataset.motion = expanded ? 'opening' : 'closing';
      let last = performance.now();
      const tick = (now: number) => {
        const dt = Math.min(Math.max((now - last) / 1000, 0), 0.064);
        last = now;
        const omega = 26;
        const displacement = state.y - target;
        const coefficient = state.velocity + omega * displacement;
        const decay = Math.exp(-omega * dt);
        state.y = target + (displacement + coefficient * dt) * decay;
        state.velocity = (state.velocity - omega * coefficient * dt) * decay;
        if (Math.abs(state.y - target) < 0.1 && Math.abs(state.velocity) < 1) {
          state.y = target; state.velocity = 0; paint();
          panel.dataset.motion = 'settled'; setMoving(false);
        } else { paint(); state.frame = requestAnimationFrame(tick); }
      };
      state.frame = requestAnimationFrame(tick);
    };
    settleRef.current = settle;
    const contentResize = () => {
      if (drag.current) { measure(); paint(); }
      else settle(openRef.current);
    };
    contentResizeRef.current = contentResize;
    const resize = () => {
      if (drag.current) {
        const { pointer, capture } = drag.current;
        drag.current = undefined; setDragging(false);
        if (capture.hasPointerCapture?.(pointer)) capture.releasePointerCapture(pointer);
      }
      if (!openRef.current) {
        cancelAnimationFrame(state.frame); measure();
        state.y = state.collapsed; state.velocity = 0; paint();
        panel.dataset.motion = 'settled'; setMoving(false);
      } else settle(true);
    };
    const scroll = () => {
      if (!openRef.current && !drag.current) {
        cancelAnimationFrame(state.frame);
        measure(); state.y = state.collapsed; state.velocity = 0; paint();
        panel.dataset.motion = 'settled'; setMoving(false);
      }
    };
    settle(openRef.current);
    const main = anchor.current.closest('.game-main-content');
    // A desktop sidebar can keep main's height unchanged while the map grows
    // and moves the anchor. Observe the map's own box as well.
    const disconnect = observeElementResize([anchor.current, main, main?.querySelector('#game-map')], resize);
    // Fitted height can change after resize delivery (including Artwork swaps).
    // Remeasure its committed custom property even when main's box stays fixed.
    const layoutObserver = new MutationObserver(resize);
    if (main) layoutObserver.observe(main, { attributes: true, attributeFilter: ['style'] });
    const disconnectContent = observeElementResize([panel, content.current, panel.querySelector('header')], contentResize);
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', scroll, true);
    window.visualViewport?.addEventListener('resize', resize);
    preference?.addEventListener?.('change', resize);
    return () => {
      cancelAnimationFrame(state.frame); disconnect(); disconnectContent(); layoutObserver.disconnect();
      const gesture = drag.current;
      drag.current = undefined;
      if (gesture?.capture.hasPointerCapture?.(gesture.pointer)) gesture.capture.releasePointerCapture(gesture.pointer);
      state.initialized = false; state.velocity = 0;
      setDragging(false); setMoving(false);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', scroll, true);
      window.visualViewport?.removeEventListener('resize', resize);
      preference?.removeEventListener?.('change', resize);
    };
  }, [available]);
  useLayoutEffect(() => { if (available) settleRef.current(open); }, [open, available]);
  useLayoutEffect(() => { if (available) contentResizeRef.current(); }, [available, expandedContent, messageText, context, actions, investigatorSheet]);

  const finishDrag = (event: React.PointerEvent, cancelled = false) => {
    const gesture = drag.current;
    if (!gesture || gesture.pointer !== event.pointerId) return;
    drag.current = undefined;
    setDragging(false);
    if (gesture.capture.hasPointerCapture?.(event.pointerId)) gesture.capture.releasePointerCapture(event.pointerId);
    if (gesture.active) suppressClick.current = true;
    if (!gesture.active || cancelled) {
      if (!cancelled && !gesture.active && event.pointerType === 'touch' && !openRef.current &&
        Math.hypot(event.clientX - gesture.startX, event.clientY - gesture.start) < 10) {
        suppressClick.current = true;
        setOpen(true);
        settleRef.current(true);
      } else settleRef.current(openRef.current);
      return;
    }
    const velocity = performance.now() - gesture.time > 100 ? 0 : gesture.velocity;
    const state = motion.current;
    const projected = state.y + (velocity / 1000) * 0.998 / (1 - 0.998);
    const next = projected < state.collapsed / 2;
    state.velocity = velocity;
    setOpen(next);
    settleRef.current(next);
  };
  if (!available) return null;
  return <>
    <div ref={anchor} className={`game-action-sheet-anchor${mobileControls ? ' has-quick-actions' : ''}${hasPreview ? ' has-preview' : ''}${messageText ? ' has-message' : ''}`} aria-hidden="true" inert>
      <div className="game-sheet-sizing-header">
        {messageText && <div className="game-sheet-collapsed-message"><span className="game-sheet-message-text">{messageText}</span></div>}
      </div>
    </div>
    {createPortal(<div className={`game-action-sheet-layer${open || dragging ? ' is-modal' : ''}${open ? ' is-open' : ''}`}
      onPointerDown={event => {
        outsidePress.current = undefined;
        if (!open || event.target !== event.currentTarget || event.button !== 0 || !isTopSheet()) return;
        event.preventDefault(); event.stopPropagation();
        outsidePress.current = { pointer: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
      }}
      onPointerMove={event => {
        const press = outsidePress.current;
        if (press && press.pointer === event.pointerId && Math.hypot(event.clientX - press.x, event.clientY - press.y) >= 10) press.moved = true;
      }}
      onPointerUp={event => {
        const press = outsidePress.current;
        if (!press || press.pointer !== event.pointerId) return;
        outsidePress.current = undefined;
        event.preventDefault(); event.stopPropagation();
        const bounds = surface.current!.getBoundingClientRect();
        const inside = event.clientX >= bounds.left && event.clientX <= bounds.right && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
        if (open && isTopSheet() && event.target === event.currentTarget && !inside && !press.moved
          && Math.hypot(event.clientX - press.x, event.clientY - press.y) < 10) {
          consumeOutsideClick();
          setOpen(false);
        }
      }}
      onPointerCancel={() => { outsidePress.current = undefined; }}
      onLostPointerCapture={() => { outsidePress.current = undefined; }}
      onClick={event => {
        if (event.target === event.currentTarget) { event.preventDefault(); event.stopPropagation(); }
      }}><div ref={surface} className={`game-ui game-info game-detail-dialog game-action-sheet${mobileControls ? ' has-quick-actions' : ''}${expandedContent ? ' is-expanded' : ''}${open ? ' is-open' : ''}${hasActions ? ' has-actions' : ''}${messageText ? ' has-message' : ''}${hasActions && !loading ? ' turn-ready' : ''}`}
      role={open ? 'dialog' : 'region'} aria-modal={open ? true : undefined} aria-label={label}
      onClickCapture={event => {
        const suppressed = suppressClick.current && event.detail > 0;
        suppressClick.current = false;
        if (suppressed || !open) {
          event.preventDefault(); event.stopPropagation();
          if (!suppressed) setOpen(true);
        }
      }}
      onPointerDownCapture={() => {
        // A drag may produce no compatibility click (for example if its opener
        // becomes hidden). Never consume the next independent press instead.
        suppressClick.current = false;
      }}
      onPointerDown={event => {
        if (event.button !== 0) return;
        const target = event.target as Element;
        if (open && (!target.closest('.game-action-sheet-header') || target.closest('button') ||
          (messageScrollable && target.closest('.game-sheet-message')))) return;
        cancelAnimationFrame(motion.current.frame);
        suppressClick.current = false;
        const capture = event.target as HTMLElement;
        drag.current = { pointer: event.pointerId, capture, start: event.clientY, startX: event.clientX, origin: motion.current.y, last: event.clientY, time: performance.now(), velocity: motion.current.velocity, active: false };
        // Capture the original target so ordinary clicks still reach its button.
        capture.setPointerCapture?.(event.pointerId);
      }}
      onPointerMove={event => {
        const gesture = drag.current;
        if (!gesture || gesture.pointer !== event.pointerId) return;
        const delta = event.clientY - gesture.start;
        if (!gesture.active && Math.abs(delta) < 10) return;
        gesture.active = true; setDragging(true); setMoving(true);
        const now = performance.now();
        gesture.velocity = (event.clientY - gesture.last) / Math.max(1, now - gesture.time) * 1000;
        gesture.last = event.clientY; gesture.time = now;
        const raw = gesture.origin + delta;
        const bound = Math.max(0, Math.min(motion.current.collapsed, raw));
        motion.current.y = bound + (raw - bound) * 0.15;
        paintRef.current();
        surface.current!.dataset.motion = 'dragging';
      }} onPointerUp={event => finishDrag(event)} onPointerCancel={event => finishDrag(event, true)} onLostPointerCapture={event => finishDrag(event, true)}
      >
      <button ref={opener} hidden={open} title={label} aria-label={label} type="button" className={`game-sheet-opener${hasActions && !loading ? ' turn-ready' : ''}`}
        aria-haspopup="dialog" aria-expanded={false} aria-controls={`${titleId}-body`} />
      <header className="game-action-sheet-header">
        {/* A click target keeps native touch hit-testing on the thin grip instead of nearby actions. */}
        <div className="game-sheet-drag-background" aria-hidden="true" onClick={() => setOpen(false)}><span className="game-sheet-grip" /></div>
        {messageText && <div ref={messageElement} className={`game-sheet-message game-sheet-collapsed-message${messageScrollable ? ' is-scrollable' : ''}`} tabIndex={open ? 0 : undefined}><span className="game-sheet-message-text">{messageText}</span></div>}
        <button type="button" className="game-dialog-close game-sheet-minimize" hidden={!open} aria-label="Minimize" title="Minimize"
          onPointerDown={event => {
            if (event.pointerType === 'touch' && event.button === 0) {
              minimizePress.current = { pointer: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
            }
          }}
          onPointerMove={event => {
            const press = minimizePress.current;
            if (press?.pointer === event.pointerId && Math.hypot(event.clientX - press.x, event.clientY - press.y) >= 10) {
              press.moved = true;
              suppressClick.current = true;
            }
          }}
          onPointerUp={event => {
            const press = minimizePress.current;
            minimizePress.current = undefined;
            if (!press || press.pointer !== event.pointerId) return;
            suppressClick.current = true;
            const box = event.currentTarget.getBoundingClientRect();
            if (press.moved || Math.hypot(event.clientX - press.x, event.clientY - press.y) >= 10 ||
              event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) return;
            // Chrome may omit the compatibility click on the first tap after
            // a captured swipe. Commit a completed touch tap directly instead.
            setOpen(false);
          }}
          onPointerCancel={() => { minimizePress.current = undefined; }}
          onLostPointerCapture={() => { minimizePress.current = undefined; }}
          onClick={() => setOpen(false)}><span aria-hidden="true">−</span></button>
      </header>
      <div id={`${titleId}-body`} className="game-action-sheet-body" inert={!open} aria-hidden={!open}>
        <div ref={content} className="game-action-sheet-content">
          <div className="game-action-sheet-preview" hidden={!hasPreview && !mobileControls}>
            {context}
            {hasActions ? actions : null}
          </div>
          <div hidden={!expandedContent}>{investigatorSheet}</div>
        </div>
      </div>
      {open && mobileControls?.row && <footer className="game-sheet-quick-controls">{mobileControls.row}</footer>}
    </div></div>, document.body)}
  </>;
}
